// Case studies data for Integrand Analytics - MCube Financial style

export const caseStudies = [
  {
    id: 1,
    title: 'Major CMBS Lender Portfolio Analysis',
    category: ['Loan Origination', 'Due Diligence'],
    description: 'Comprehensive loan underwriting and due diligence support for a portfolio of 150+ commercial properties worth over $2 billion in total loan value.',
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80',
    slug: 'cmbs-portfolio-analysis',
    client: 'Leading CMBS Lender',
    duration: '6 months',
    value: '$2B+ Portfolio',
    details: {
      overview: 'A leading CMBS lender required comprehensive underwriting support for their commercial real estate portfolio spanning multiple property types across the United States. The project involved detailed financial analysis, lease reviews, and risk assessment for over 150 properties.',
      challenge: 'The client faced tight timelines for loan closings while maintaining their high standards for due diligence. They needed a reliable partner who could scale operations quickly while ensuring accuracy.',
      solution: 'Our team deployed 12 dedicated analysts who worked in parallel streams to process the portfolio. We implemented custom quality control checkpoints and real-time reporting dashboards to maintain transparency throughout the engagement.',
      outcome: 'Delivered 100% of loan packages on time with 99.5% accuracy. The client was able to close their funding round on schedule and has since engaged us for ongoing portfolio monitoring.',
      team: '12 FTE for 6 months',
      technologies: ['ARGUS', 'Excel Financial Modeling', 'Power BI', 'Custom QC Tools']
    }
  },
  {
    id: 2,
    title: 'National REIT Lease Abstraction',
    category: ['Lease Administration', 'Data Digitization'],
    description: 'Large-scale lease abstraction project covering 3,000+ commercial leases across retail, office, and industrial properties for a Fortune 500 REIT.',
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80',
    slug: 'reit-lease-abstraction',
    client: 'Fortune 500 REIT',
    duration: '8 months',
    value: '3,000+ Leases',
    details: {
      overview: 'A Fortune 500 REIT required comprehensive lease abstraction services as part of their enterprise system migration. The project involved abstracting critical data points from over 3,000 commercial leases.',
      challenge: 'Legacy lease documents were stored in various formats across multiple systems. Many documents were scanned images requiring OCR processing, and critical date tracking was essential for business continuity.',
      solution: 'We developed a structured abstraction template capturing 50+ data points per lease. Our team processed documents in batches with dedicated QC reviewers ensuring data accuracy before system upload.',
      outcome: 'Successfully abstracted all leases with 98%+ accuracy rate. The client achieved full system migration on schedule with minimal business disruption.',
      team: '8 FTE for 8 months',
      technologies: ['Yardi', 'MRI Software', 'Custom Templates', 'OCR Processing']
    }
  },
  {
    id: 3,
    title: 'Investment Bank Deal Pipeline Support',
    category: ['Financial Modeling', 'Consulting'],
    description: 'Ongoing financial modeling and analysis support for a global investment banks real estate advisory practice handling $5B+ in annual transactions.',
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80',
    slug: 'investment-bank-modeling',
    client: 'Global Investment Bank',
    duration: 'Ongoing (24+ months)',
    value: '$5B+ Annual Transactions',
    details: {
      overview: 'A global investment bank required dedicated financial modeling support for their real estate advisory practice. The engagement involves creating bespoke financial models for complex CRE transactions.',
      challenge: 'The bank needed quick turnaround on complex financial models while maintaining their rigorous accuracy standards. Deal flow was unpredictable, requiring flexible resource allocation.',
      solution: 'We established a dedicated offshore team with deep expertise in investment banking models. The team operates as an extension of the clients in-house team with seamless communication protocols.',
      outcome: 'Supported 200+ transactions with average model turnaround of 48 hours. The engagement has expanded from 2 analysts to a team of 10 over two years.',
      team: '10 FTE ongoing',
      technologies: ['Excel', 'ARGUS Enterprise', 'CoStar', 'Bloomberg Terminal']
    }
  },
  {
    id: 4,
    title: 'Regional Bank Loan Servicing Platform',
    category: ['Technology', 'Loan Servicing'],
    description: 'Implementation of automated loan servicing workflows and custom reporting dashboards for a regional banks $800M CRE portfolio.',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80',
    slug: 'bank-servicing-platform',
    client: 'Regional Bank',
    duration: '4 months',
    value: '$800M Portfolio',
    details: {
      overview: 'A regional bank sought to modernize their CRE loan servicing operations with automated workflows and real-time portfolio visibility through custom dashboards.',
      challenge: 'Manual processes led to delayed financial reporting and increased operational risk. The bank needed a solution that integrated with their existing core banking system.',
      solution: 'We designed and implemented custom Salesforce workflows integrated with their loan servicing platform. Custom Power BI dashboards provided real-time portfolio analytics.',
      outcome: 'Reduced monthly reporting time by 60% and improved data accuracy to 99%+. The bank now has real-time visibility into portfolio performance and risk metrics.',
      team: '5 FTE for 4 months',
      technologies: ['Salesforce', 'Power BI', 'API Integration', 'Custom Automation']
    }
  },
  {
    id: 5,
    title: 'Private Equity Quarterly Reporting',
    category: ['Financial Analysis', 'Reporting'],
    description: 'Ongoing quarterly financial analysis and investor reporting for a private equity funds $1.5B commercial real estate portfolio across 40+ assets.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
    slug: 'pe-quarterly-reporting',
    client: 'Private Equity Fund',
    duration: 'Ongoing (36+ months)',
    value: '$1.5B AUM',
    details: {
      overview: 'A private equity fund required comprehensive quarterly financial analysis and investor reporting support for their diversified CRE portfolio.',
      challenge: 'Investor reporting required detailed property-level analysis while maintaining consistent presentation across a diverse portfolio of asset types.',
      solution: 'We established standardized analysis templates and reporting frameworks that accommodate different property types while ensuring consistent investor-grade deliverables.',
      outcome: 'Delivered 12+ quarters of investor reports with zero restatements. The fund has expanded our scope to include acquisition due diligence support.',
      team: '4 FTE ongoing',
      technologies: ['Excel', 'ARGUS', 'InvestorFlow', 'Custom Templates']
    }
  },
  {
    id: 6,
    title: 'Mortgage Broker Loan Sizing Platform',
    category: ['Loan Origination', 'Technology'],
    description: 'Development of automated loan sizing tools and deal packaging workflows for a national commercial mortgage brokerage.',
    image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800&q=80',
    slug: 'broker-loan-sizing',
    client: 'National Mortgage Broker',
    duration: '3 months',
    value: '500+ Deals/Year',
    details: {
      overview: 'A national commercial mortgage broker needed to standardize their loan sizing process and create consistent deal packages for lender submissions.',
      challenge: 'Inconsistent deal packages were causing delays in lender responses and reducing win rates on competitive transactions.',
      solution: 'We developed standardized loan sizing templates and automated deal packaging workflows that produce consistent, professional submissions.',
      outcome: 'Reduced deal package preparation time by 70% and improved lender response rates by 25%. The broker has increased deal volume while reducing operational staff.',
      team: '3 FTE for 3 months',
      technologies: ['Excel VBA', 'PDF Automation', 'Custom Templates']
    }
  }
];

export const categories = [
  'All',
  'Loan Origination',
  'Due Diligence',
  'Lease Administration',
  'Data Digitization',
  'Financial Modeling',
  'Consulting',
  'Technology',
  'Loan Servicing',
  'Financial Analysis',
  'Reporting'
];
